/**
 * BulkAddPreviewPane — Stage 2 of the bulk-add flow (Block 6.E).
 *
 * Composes the 3-block preview per ADR-017 §"Bulk-add preview-pane shape":
 *   1. New organizations (per-row preview)
 *   2. Possible duplicates (per-row Yes/No choice)
 *   3. Lookup taxonomy additions (per-paste explicit approval checkbox)
 *
 * Single Confirm gate fires onCommit with the operator's resolved
 * decisions. Confirm disabled when:
 *   - Any row with duplicate candidates has unmade decision.
 *   - pending_lookup_adds.length > 0 AND approval checkbox unchecked.
 *   - All rows have errors (nothing to commit).
 */

import { useMemo, useState, type ReactNode } from 'react';
import type {
  BulkAddCommitRow,
  BulkAddParsedRowPreview,
  BulkAddPendingLookupAdd,
  ProviderKindForUI,
} from '@/lib/types';
import { BulkAddRowPreview } from './BulkAddRowPreview';
import { BulkAddDuplicateChoice } from './BulkAddDuplicateChoice';
import { BulkAddLookupAddsBlock } from './BulkAddLookupAddsBlock';

export interface BulkAddPreviewPaneProps {
  rows: ReadonlyArray<BulkAddParsedRowPreview>;
  pendingLookupAdds: ReadonlyArray<BulkAddPendingLookupAdd>;
  busy?: boolean;
  errorMessage?: string | null;
  onEditPaste: () => void;
  onCommit: (input: {
    rows: BulkAddCommitRow[];
    pending_lookup_adds: BulkAddPendingLookupAdd[];
    approved_lookup_seeding: boolean;
  }) => void;
}

export function BulkAddPreviewPane({
  rows,
  pendingLookupAdds,
  busy = false,
  errorMessage = null,
  onEditPaste,
  onCommit,
}: BulkAddPreviewPaneProps): ReactNode {
  // Per-row duplicate decisions: undefined=unmade; null=create separate;
  // string=use existing (skip add).
  const [dupDecisions, setDupDecisions] = useState<Record<number, string | null | undefined>>({});

  // Lookup approval checkbox state.
  const [lookupApproved, setLookupApproved] = useState(false);

  const rowsWithDuplicates = useMemo(
    () => rows.filter((r) => r.duplicate_candidates.length > 0 && r.errors.length === 0),
    [rows],
  );

  const allDupDecisionsMade = rowsWithDuplicates.every(
    (r) => dupDecisions[r.row_index] !== undefined,
  );
  const lookupGateOk = pendingLookupAdds.length === 0 || lookupApproved;
  const validRowCount = rows.filter((r) => r.errors.length === 0).length;

  const canCommit =
    !busy && validRowCount > 0 && allDupDecisionsMade && lookupGateOk;

  function handleConfirm(): void {
    const commitRows: BulkAddCommitRow[] = rows.map((r) => {
      // Decide disposition + kind.
      const dupDecision = dupDecisions[r.row_index];
      let disposition: 'create' | 'skip_as_duplicate' | 'skip_with_error';
      let duplicate_of_org_id: string | undefined;
      if (r.errors.length > 0) {
        disposition = 'skip_with_error';
      } else if (typeof dupDecision === 'string') {
        disposition = 'skip_as_duplicate';
        duplicate_of_org_id = dupDecision;
      } else {
        disposition = 'create';
      }
      // kind: explicit value if known + present; default 'other' when blank.
      const kind: ProviderKindForUI =
        r.kind_raw && r.kind_known
          ? (r.kind_raw as ProviderKindForUI)
          : 'other';
      return {
        row_index: r.row_index,
        name: r.name ?? '',
        phone: r.phone,
        address: r.address,
        category_slug: r.category_slug ?? '',
        subcategory_slugs: [...r.subcategory_slugs],
        service_area_slugs: [...r.service_area_slugs],
        kind,
        disposition,
        duplicate_of_org_id,
      };
    });

    onCommit({
      rows: commitRows,
      pending_lookup_adds: [...pendingLookupAdds],
      approved_lookup_seeding: lookupApproved,
    });
  }

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">
        Bulk add: {validRowCount} organization{validRowCount === 1 ? '' : 's'}
      </h3>

      {/* Block 1 — New organizations */}
      <section>
        <div className="text-sm font-semibold border-b border-sage-800/[8%] pb-1 mb-2">
          New organizations ({validRowCount})
          {rows.length - validRowCount > 0 && (
            <span className="text-red-700 ml-2">
              · {rows.length - validRowCount} with errors
            </span>
          )}
        </div>
        <ul className="bg-sand border border-sage-800/[8%] rounded-md px-3">
          {rows.map((r) => (
            <BulkAddRowPreview
              key={r.row_index}
              row={r}
              disposition={
                r.errors.length > 0
                  ? 'skip_with_error'
                  : typeof dupDecisions[r.row_index] === 'string'
                    ? 'skip_as_duplicate'
                    : 'create'
              }
            />
          ))}
        </ul>
      </section>

      {/* Block 2 — Possible duplicates */}
      {rowsWithDuplicates.length > 0 && (
        <section>
          <div className="text-sm font-semibold border-b border-sage-800/[8%] pb-1 mb-2">
            Possible duplicates ({rowsWithDuplicates.length})
          </div>
          {rowsWithDuplicates.map((r) => (
            <BulkAddDuplicateChoice
              key={r.row_index}
              rowIndex={r.row_index}
              pastedName={r.name ?? '(no name)'}
              candidates={r.duplicate_candidates}
              decision={dupDecisions[r.row_index]}
              onDecide={({ duplicateOfOrgId }) =>
                setDupDecisions((prev) => ({
                  ...prev,
                  [r.row_index]: duplicateOfOrgId,
                }))
              }
            />
          ))}
        </section>
      )}

      {/* Block 3 — Lookup taxonomy additions */}
      <BulkAddLookupAddsBlock
        pendingAdds={pendingLookupAdds}
        approved={lookupApproved}
        onToggleApproved={setLookupApproved}
      />

      {errorMessage && (
        <div role="alert" className="text-sm text-red-700" data-testid="bulk-add-commit-error">
          ⚠ {errorMessage}
        </div>
      )}

      <div className="flex gap-2 justify-between border-t border-sage-800/[8%] pt-3">
        <button
          type="button"
          onClick={onEditPaste}
          disabled={busy}
          className="min-h-11 px-3 py-2 text-sm text-ink-soft hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          ← Edit paste
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canCommit}
          className="min-h-11 px-4 py-2 text-sm bg-green-600 text-cream rounded-md hover:bg-green-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {busy ? 'Creating…' : 'Approve & Create →'}
        </button>
      </div>
    </div>
  );
}
